import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useFetch, useUpdate } from "../../hooks";
import { BASE_URL_TEST } from "../../utils/baseUrl";
import { urls } from "../../urls/urls";
import { convertTimeStampToDate } from "../../utils/DateFormats";

const OrderCard = ({ order }) => {
  if (!order) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", xl: 1800 },
        display: "flex",
        flexDirection: "column",
        p: 0,
        m: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "#E6533C",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: 2,
          p: 2,
          m: 0,
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          Order ID: {order.cart_number}
        </Typography>
      </Box>

      <CardContent>
        {order.vendors?.map((vendor) => {
          const isCancelled =
            vendor.order_status?.toLowerCase().trim() === "cancelled";

          if (!isCancelled) return null;

          return (
            <Box key={vendor.cart_vendor_id} sx={{ mb: 4 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Vendor Order ID: {vendor.cart_number}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Transaction Date:{" "}
                  {order?.transaction_date_time
                    ? convertTimeStampToDate(order.transaction_date_time)
                    : "--"}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  Distance: {vendor?.distance ?? "--"}
                </Typography>
              </Box>

              {vendor.items?.map((item) => (
                <Box
                  key={item.cart_item_id}
                  display="flex"
                  alignItems="center"
                  gap={3}
                  sx={{ mb: 3 }}
                >
                  <img
                    src={item.listing?.images?.[0]}
                    alt={item.listing?.name}
                    style={{ width: 80, height: 80, borderRadius: 10 }}
                  />
                  <Box>
                    <Typography
                      variant="h7"
                      fontWeight="bold"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.listing?.name}
                    </Typography>
                    <Typography variant="body1">
                      Qty: {item.quantity}
                    </Typography>
                  </Box>
                  <Box ml="auto" textAlign="right">
                    {item.price && (
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <span>₹</span> <span>{item.price}</span>
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Reorder Button */}
              {/* <Box display="flex" justifyContent="flex-start" gap={2} mt={2}>
                <Button
                  color="secondary"
                  sx={{ borderRadius: 2, textTransform: "none" }}
                >
                  ReOrder
                </Button>
              </Box> */}
            </Box>
          );
        })}
      </CardContent>

      <Divider sx={{ my: 2 }} />
    </Box>
  );
};

const CancelledListing = () => {
  const customerId = useSelector((state) => state.user.customerId);
  const apptoken = useSelector((state) => state.user.apptoken);
  const [cancelledOrdersList, setCancelledOrdersList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const {
    data: orderData,
    error: orderError,
    fetchData: fetchOrdersData,
  } = useFetch(BASE_URL_TEST + urls("getListingOrdersByUserId", customerId), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: apptoken ? `Bearer ${apptoken}` : "",
    },
  });

  useEffect(() => {
    if (customerId) {
      fetchOrdersData();
    }
  }, [customerId]);

  useEffect(() => {
    if (orderError) {
      console.error("Order fetch error:", orderError);
      setErrorMessage("Failed to fetch orders.");
      setCancelledOrdersList([]);
    } else if (orderData) {
      if (orderData.status === true) {
        const filteredOrders = orderData.data
          .filter((order) =>
            order.vendors?.some(
              (vendor) =>
                typeof vendor.order_status === "string" &&
                vendor.order_status.trim().toLowerCase() === "cancelled"
            )
          )
          .sort(
            (a, b) =>
              new Date(b.transaction_date_time) -
              new Date(a.transaction_date_time)
          );
        setCancelledOrdersList(filteredOrders);
        setErrorMessage("");
      } else {
        setCancelledOrdersList([]);
        setErrorMessage(orderData.message || "No orders found.");
      }
    }
  }, [orderData, orderError]);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Cancelled Orders
      </Typography>
      <Card>
        <Box display="flex" flexDirection="column" gap={5} p={2}>
          {errorMessage && (
            <Typography color="error">{errorMessage}</Typography>
          )}

          {cancelledOrdersList.length === 0 && !errorMessage && (
            <Typography>No cancelled orders found.</Typography>
          )}

          {cancelledOrdersList.map((order) => (
            <OrderCard key={order.cart_id} order={order} />
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default CancelledListing;
